/**
 * Route definition for /wallet/$address
 *
 * Uses TanStack Router's loader to prefetch wallet data server-side.
 * The wallet data is fetched before the component renders, providing
 * instant page loads and built-in loading states.
 *
 * @module routes/wallet/$address
 * @category Routes
 */

import { createFileRoute, redirect } from '@tanstack/react-router'
import { toast } from 'react-toastify'
import { getWalletData, ApiError } from '@/lib/api'
import WalletContainer from '@/containers/Wallet'
import { createProfileSchema } from '@/utils/metadata'
import { setPageMetadata } from '@/utils/page-metadata'

/**
 * Loader function that runs before the component renders.
 *
 * Server-side: fetches wallet data and returns it
 * Client-side: provides cached data if available, refetches if stale
 *
 * @param {Object} params - Route parameters
 * @param {string} params.address - The wallet address from the URL
 * @returns {Promise<{initialWalletData: any, address: string}>}
 */
export const Route = createFileRoute('/$locale/wallet/$address')({
  /**
   * Preloads the wallet data before component mount.
   * Supports SSR - data is fetched on server during initial render
   * and hydrated on client for seamless transition.
   */
  loader: async ({ params: { address } }) => {
    // Validate address exists
    if (!address) {
      throw new Error('Wallet address is required')
    }

    try {
      // Fetch wallet data using our typed API
      const walletData = await getWalletData(address)

      // Return data to be available via Route.useLoaderData()
      return {
        initialWalletData: walletData,
        address,
      }
    } catch (error) {
      // Log error and show toast, then propagate to error boundary
      console.error('Failed to load wallet data:', error)
      toast.error('Failed to load wallet information. Please try again.')
      throw error
    }
  },

  /**
   * The component that renders the wallet page.
   * Receives loader data via Route.useLoaderData() hook.
   */
  component: WalletPage,
})

/**
 * Wallet page component.
 *
 * Receives preloaded wallet data from the loader and passes it to
 * the WalletContainer. This eliminates the need for useEffect-based
 * data fetching and provides instant rendering.
 *
 * @returns {React.ReactElement}
 */
function WalletPage() {
  // Get the preloaded data from the route loader
  const { initialWalletData, address } = Route.useLoaderData()

  const pageUrl = `https://stealthisnft.xyz/wallet/${address}`

  // Wallet name and bio
  const walletName = initialWalletData?.username || initialWalletData?.display_name || `${address.slice(0, 8)}...${address.slice(-8)}`
  const walletBio = initialWalletData?.bio || `Wallet address: ${address}`

  // Estimate counts from wallet data
  const ownedCount = initialWalletData?.nfts?.length || initialWalletData?.ownedCount || 0
  const createdCount = initialWalletData?.createdCount || initialWalletData?.collections?.length || 0

  // Generate profile schema for the wallet (treated as a user profile)
  const structuredData = createProfileSchema({
    name: walletName,
    bio: walletBio,
    url: pageUrl,
    image: initialWalletData?.avatar || '',
    address: address,
    ownedCount: ownedCount,
    createdCount: createdCount,
  })

  // Set metadata including JSON-LD
  setPageMetadata({
    title: `${walletName} | VIRI`,
    description: walletBio,
    ogImage: initialWalletData?.avatar || undefined,
    ogType: 'profile',
    twitterCard: 'summary_large_image',
    jsonLd: structuredData,
  })

  return (
    <WalletContainer initialWalletData={initialWalletData} address={address} />
  )
}
